import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom, Observable } from "rxjs";
import { LandingPage } from '../../../../../../../apps/fourcomply-dashboard/src/app/shared/models/landing-page.model';
import { ClientSerializer } from "../../../models/clients/client-serializer.class";
import { Client } from "../../../models/clients/client.model";
import { KeyValuePair } from "../../../models/key-value.interface";
import { BaseApiService } from "../base/base-api.service";
import { CustomErrorHandlerService } from "../custom-error-handler.service";

/**
 * Service to communicate with API Clients/Tenants
 */

@Injectable()
export class ClientsService extends BaseApiService<Client> {
  crntSerializer = new ClientSerializer();
  constructor(httpClient: HttpClient) {
    super(
      httpClient,
      null,
      "client",
      new ClientSerializer(),
      new CustomErrorHandlerService()
    );
  }

  createClient(name: any, tenant_suffix: any, current_user: any): Observable<any> {
    return this.httpClient.post(`${this.url}/client`, {
      name,
      tenant_suffix,
      external_id: current_user.sub.replace("auth0|", ""),
      existing_user: true,
    });
  }

  resetClient(id: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/client/${id}/reset-workflow-hitl-email`,
      {}
    );
  }

  resetClientSpecific(id: any, config: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/client/${id}/reset-specific`,
      config
    );
  }

  updateCustomFields(clientId: any, newCustomFields: any): Observable<any> {
    return this.httpClient.put(
      `${this.url}/client/${clientId}/customfieldnames`,
      {
        custom_field_1_name: newCustomFields.customFieldName1,
        custom_field_2_name: newCustomFields.customFieldName2,
        custom_field_3_name: newCustomFields.customFieldName3,
        custom_field_4_name: newCustomFields.customFieldName4,
        custom_field_5_name: newCustomFields.customFieldName5,
        custom_field_6_name: newCustomFields.customFieldName6,
        custom_field_7_name: newCustomFields.customFieldName7,
      }
    );
  }

  getDomainCNameInfo(id: number, domain: string): Observable<any> {
    return this.httpClient.get(`${this.url}/client/${id}/domain/${domain}`);
  }

  getDefaultEmail(id: string): Observable<any> {
    return this.httpClient.get(`${this.url}/client/${id}/defaultEmail`);
  }

  updateDefaultEmail(id: string, email: string): Observable<any> {
    return this.httpClient.put(`${this.url}/client/${id}/defaultEmail`, {
      default_email_address: email,
    });
  }

  getSecondaryEmails(id: string): Observable<any> {
    return this.httpClient.get(`${this.url}/client/${id}/secondaryEmails`);
  }

  updateSecondaryEmails(id: string, emails: string): Observable<any> {
    return this.httpClient.put(`${this.url}/client/${id}/secondaryEmails`, {
      secondary_email_addresses: emails,
    });
  }

  updateAlias(clientId: string, alias: string) {
    return this.httpClient.put(`${this.url}/client/${clientId}/alias`, {
      alias: alias,
    });
  }

  updatePermissionUpdateLogic(clientId: string, logic: string) {
    return this.httpClient.put(
      `${this.url}/client/${clientId}/permissionUpdateLogic`,
      {
        permission_update_logic: logic,
      }
    );
  }

  uploadLogo(clientId: any, file: any): Observable<any> {
    const form: any = {};
    form.files = [file];
    return this.httpClient.post(
      `${this.url}/client/upload-logo/${clientId}`,
      form
    );
  }

  getUploadLogoUrl(clientId: any, contentType: any, extension: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/client/${clientId}/logo/uploadurl`,
      {
        content_type: contentType,
        extension,
      }
    );
  }

  uploadFavicon(clientId: any, file: any): Observable<any> {
    const form: any = {};
    form.files = [file];
    return this.httpClient.post(
      `${this.url}/client/upload-favicon/${clientId}`,
      form
    );
  }

  getUploadFaviconUrl(clientId: any, contentType: any, extension: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/client/${clientId}/favicon/uploadurl`,
      {
        content_type: contentType,
        extension,
      }
    );
  }

  // The uploadurl endpoints no longer write the reference to the tenant: they hand back the
  // key they signed and wait. Until one of these confirmations lands, the tenant keeps the
  // asset it had, so a failed or abandoned upload can no longer leave it pointing at an
  // object that was never stored (BUG-068).
  public confirmLogo(clientId: string, key: string): Observable<unknown> {
    return this.httpClient.put(`${this.url}/client/${clientId}/logo`, { key });
  }

  public confirmFavicon(clientId: string, key: string): Observable<unknown> {
    return this.httpClient.put(`${this.url}/client/${clientId}/favicon`, { key });
  }

  public deleteLogo(clientId: string): Observable<unknown> {
    return this.httpClient.delete(`${this.url}/client/${clientId}/logo`);
  }

  public deleteFavicon(clientId: string): Observable<unknown> {
    return this.httpClient.delete(`${this.url}/client/${clientId}/favicon`);
  }

  public async getLandingPages(): Promise<LandingPage[]> {
    const result: any = await firstValueFrom(this.httpClient.get(`${this.url}/landingpages`));
    return result?.data.pages as LandingPage[];
  }

  public async createLandingPage(landingPage: any): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${this.url}/landingpages`, landingPage));
  }

  public async updateLandingPage(landingPage: any): Promise<void> {
    await firstValueFrom(this.httpClient.put(
      `${this.url}/landingpages/${landingPage.id}`,
      landingPage
    ));
  }

  getPermissionTypes(clientId: any): Observable<any> {
    return this.httpClient.get(
      `${this.url}/client/${clientId}/permissiontypes`
    );
  }

  updatePermissionTypes(clientId: any, newPermissionTypes: any): Observable<any> {
    return this.httpClient.put(
      `${this.url}/client/${clientId}/permissiontypes`,
      {
        explicit_permission_types: newPermissionTypes,
      }
    );
  }

  getPermissionCategories(clientId: any): Observable<any> {
    return this.httpClient.get(
      `${this.url}/client/${clientId}/permissioncategories`
    );
  }
  getComplianceInputTypes(clientId: any): Observable<any> {
    return this.httpClient.get(
      `${this.url}/client/${clientId}/complianceinputtypes`
    );
  }

  updatePermissionCategories(clientId: any, newPermissionCategories: any): Observable<any> {
    return this.httpClient.put(
      `${this.url}/client/${clientId}/permissioncategories`,
      {
        permission_categories: newPermissionCategories,
      }
    );
  }

  createClientRegulations(clientId: any, newClientRegulations: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/client/${clientId}/clientregulations`,
      {
        client_regulations: newClientRegulations,
      }
    );
  }

  public async getLogo(clientId: any): Promise<any> {
    return await firstValueFrom(this.httpClient.get(`${this.url}/client/${clientId}/logo`));
  }

  getFavicon(clientId: any) {
    return this.httpClient.get(`${this.url}/client/${clientId}/favicon`);
  }

  getToken(): Observable<KeyValuePair> {
    return this.httpClient.get<KeyValuePair>(`${this.url}/client/gettoken`);
  }

  updateToken() {
    return this.httpClient.post<KeyValuePair>(
      `${this.url}/client/updatetoken`,
      ""
    );
  }
}
